import type { FacilityDto } from "@ckweb/ckfetch/connectables/setup";
import type {
  LineDto,
  WorkGroupDto,
} from "@ckweb/ckfetch/connectables/equipmentAreaManagement";
import type { GridConfig } from "@/types/grid";
import type { BulkImportPageSetupStatic } from "@/types/bulk-import";
import type { LookUpViewNumber } from "@ckweb/ckfetch/connectables/transactions";
import type { InspZoneDto } from "@ckweb/ckfetch/connectables/inspectionManagement";

// TODO: Taken from src/ckClient/composables/taskHeights.ts
export enum TaskHeights {
  Ceiling = 5,
  AboveAsset = 4,
  Asset = 3,
  Floor = 2,
  Drains = 1,
  Exterior = 0,
}

function getWeekdayList(
  // by default does not translate
  t: I18nTranslate = $ck.utils.PASS_THROUGH,
): ValueLabelPair<number>[] {
  // translation will be handled on the dropdown itself
  return $ck.utils.getLocalizedWeekdayArray(t).map((label, index) => ({
    label,
    value: index + 1,
  }));
}

export const getEquipmentAreasPageConfig =
  (): BulkImportPageSetupStatic<EquipmentAreaSchema> => ({
    id: "equipment-areas",
    pageTitle: "cksetup.imports.equipment-area.pageTitle",
    pageHeading: "cksetup.imports.equipment-area.heading",
    learnMoreTitle: "cksetup.imports.equipment-area.learnMoreTitle",
    learnMoreText: "cksetup.imports.equipment-area.learnMoreText",
    templateFileName: "cksetup.imports.equipment-area.templateName",
    templateWorksheetName: "tableColumns.equipmentAreas.worksheetName",
    tabTitle: "cksetup.imports.bulk.equipmentAreasHeading",
    tabDescription: "cksetup.imports.bulk.equipmentAreasDescription",
    imageSrc: "/static/images/bulk/equipment-areas.png",
    tabTooltip: "cksetup.imports.bulk.equipmentAreasTooltip",
    validationSchema: equipmentAreaSchema,
    addButtonLabel: "cksetup.imports.equipment-area.addNewEquipmentAreaLine",
  });

export const getEquipmentAreasGridConfig = async ({
  facilityDto,
  productionLines,
  workGroups,
  taskHeights,
  inspectionZones,
}: {
  facilityDto: FacilityDto;
  productionLines: LineDto[];
  workGroups: WorkGroupDto[];
  taskHeights: LookUpViewNumber[];
  inspectionZones: InspZoneDto[];
}): Promise<GridConfig<EquipmentAreaSchema>> => {
  const weekDayOptions = getWeekdayList();
  const defaultRowData: Partial<EquipmentAreaSchema> = {
    active: true,
    dayNumber: 1,
    isAsset: true,
    location: "",
    corpEquipNum: "",
    corporateReportUse: 0,
    isNoDelete: false,
    corporateLock: false,
    mobileGenerateSheet: false,
    hasNoTasks: false,
    noTaskForward: false,
    taskHeight:
      taskHeights.find((item) => item.number === TaskHeights.Asset)?.number ||
      0,
    includeInInspections: true,
    includeInWo: true,
    workpackDayNumber: 2, // Monday
    corporateCategoryNumber:
      facilityDto.corporateCategories.find(({ name }) => name.startsWith("PIC"))
        ?.number ?? facilityDto.corporateCategories[0]?.number,
    model: inspectionZones.find((item) => item.default)?.number?.toString(),
  };

  return {
    computeItemFn: (item) => {
      const productionLine = productionLines?.find(
        (line) => line.active && line.number === item.lineNumber,
      );
      const workGroup = workGroups?.find(
        (workGroup) =>
          workGroup.active && workGroup.number === item.workGroupNumber,
      );
      const corporateCategory = facilityDto?.corporateCategories.find(
        (category) => category.number === item.corporateCategoryNumber,
      );
      const model = item.model ?? defaultRowData.model;

      return {
        ...defaultRowData, // when importing data, defaults are needed for validation not to fail
        ...item,
        lineName: productionLine?.name ?? "",
        workGroupName: workGroup?.name ?? "",
        corporateCategoryName: corporateCategory?.name ?? "",
        changeoverLevel: workGroup?.changeoverLevel,
        model,
      };
    },
    defaultRowData,
    columns: [
      {
        field: "number",
        alternativeMappingNames: ["id"],
        hidden: true,
        isKey: true,
        propagateErrorsTo: "name",
      },
      {
        field: "name",
        title: "tableColumns.name",
        helperText: "tableColumns.equipmentAreas.nameHelperText",
        editor: "text",
        placeholder: "tableColumns.enterName",
        minWidthPx: 250,
      },
      {
        field: "isAsset",
        title: "tableColumns.equipmentAreas.isAsset",
        helperText: "tableColumns.equipmentAreas.isAssetHelperText",
        editor: "boolean",
      },
      {
        field: "active",
        title: "tableColumns.activeColumnLabel",
        helperText: "tableColumns.equipmentAreas.activeHelperText",
        editor: "boolean",
      },
      {
        field: "lineNumber",
        title: "tableColumns.lineName",
        helperText: "tableColumns.equipmentAreas.lineNameHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: productionLines
            .filter(({ active }) => active)
            .map((item) => ({
              label: item.name,
              value: item.number,
            })),
        },
        minWidthPx: 140,
      },
      {
        field: "lineName",
        hidden: true,
        propagateErrorsTo: "lineNumber",
      },
      {
        field: "dayNumber",
        hidden: true,
      },
      {
        field: "location",
        title: "tableColumns.location",
        helperText: "tableColumns.equipmentAreas.locationHelperText",
        editor: "text",
        placeholder: "tableColumns.enterLocation",
        required: false,
      },
      {
        field: "workGroupNumber",
        title: "tableColumns.equipmentAreas.workGroupName",
        helperText: "tableColumns.equipmentAreas.workGroupNameHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: workGroups
            .filter(({ active }) => active)
            .map((item) => ({
              label: item.name,
              value: item.number,
            })),
        },
        minWidthPx: 200,
      },
      {
        field: "workGroupName",
        hidden: true,
        propagateErrorsTo: "workGroupNumber",
      },
      {
        field: "changeoverLevel",
        hidden: true,
        propagateErrorsTo: "workGroupNumber",
      },
      {
        field: "hasNoTasks",
        hidden: true,
      },
      {
        field: "critical",
        title: "tableColumns.critical",
        helperText: "tableColumns.equipmentAreas.criticalHelperText",
        editor: "boolean",
      },
      {
        field: "corpEquipNum",
        hidden: true,
      },
      {
        field: "includeInInspections",
        title: "tableColumns.includeInInspections",
        helperText:
          "tableColumns.equipmentAreas.includeInInspectionsHelperText",
        editor: "boolean",
        minWidthPx: 210,
      },
      {
        field: "workpackDayNumber",
        title: "tableColumns.equipmentAreas.workpackDay",
        helperText: "tableColumns.equipmentAreas.workpackDayHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: weekDayOptions,
          translateItems: true,
        },
        minWidthPx: 260,
      },
      {
        field: "includeInWo",
        title: "tableColumns.equipmentAreas.includeInWo",
        helperText: "tableColumns.equipmentAreas.includeInWoHelperText",
        editor: "boolean",
        minWidthPx: 210,
      },
      {
        field: "corporateCategoryNumber",
        alternativeMappingNames: [
          "Corporate KPI Number",
          "Corporate KPI Name",
          "Corporate KPI",
        ],
        title: "tableColumns.equipmentAreas.corporateCategoryName",
        helperText:
          "tableColumns.equipmentAreas.corporateCategoryNameHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: facilityDto.corporateCategories
            // .filter(({ active }) => active) // TODO: all of them are inactive for some reason (BE)
            .map((item) => ({
              label: item.name,
              value: item.number,
            })),
        },
        minWidthPx: 310,
      },
      {
        field: "corporateCategoryName",
        hidden: true,
        propagateErrorsTo: "corporateCategoryNumber",
      },
      {
        field: "taskHeight",
        title: "tableColumns.equipmentAreas.taskHeight",
        helperText: "tableColumns.equipmentAreas.taskHeightHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: taskHeights.map((item) => ({
            label: item.name,
            value: item.number,
          })),
        },
        minWidthPx: 140,
      },
      {
        field: "model",
        title: "tableColumns.equipmentAreas.inspectionZone",
        helperText: "tableColumns.equipmentAreas.inspectionZoneHelperText",
        editor: "select",
        placeholder: "tableColumns.select",
        editorProps: {
          items: inspectionZones.map((item) => ({
            label: item.description,
            value: item.number.toString(),
            disabled: !item.active,
          })),
        },
        minWidthPx: 310,
      },
      {
        field: "mobileGenerateSheet",
        hidden: true,
      },
      {
        field: "noTaskForward",
        hidden: true,
      },
      {
        field: "corporateReportUse",
        hidden: true,
      },
      {
        field: "corporateLock",
        hidden: true,
      },
      {
        field: "isNoDelete",
        hidden: true,
      },
    ],
  };
};
